import React, { useEffect, useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { API } from '@/App';
import DashboardLayout from '@/components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { QrCode, Plus, Copy, CheckCircle, Clock, XCircle, Filter, Download, Eye } from 'lucide-react';
import { toast } from 'sonner';
import { format } from 'date-fns';

export default function ValidationCodes() {
  const { user } = useAuth();
  const [codes, setCodes] = useState([]);
  const [branches, setBranches] = useState([]);
  const [stats, setStats] = useState({ active: 0, used: 0, expired: 0, total: 0 });
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [qrDialogOpen, setQrDialogOpen] = useState(false);
  const [selectedCode, setSelectedCode] = useState(null);
  const [filterBranch, setFilterBranch] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [formData, setFormData] = useState({
    branch_id: '',
    code_type: 'alphanumeric',
    quantity: 1
  });

  useEffect(() => {
    fetchBranches();
    fetchStats();
  }, []);

  useEffect(() => {
    fetchCodes();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterBranch, filterStatus]);

  const fetchBranches = async () => {
    try {
      const response = await fetch(`${API}/branches`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setBranches(data);
      }
    } catch (error) {
      console.error('Error fetching branches:', error);
    }
  };

  const fetchCodes = async () => {
    try {
      let url = `${API}/validation-codes`;
      const params = new URLSearchParams();
      if (filterBranch && filterBranch !== 'all') params.append('branch_id', filterBranch);
      if (filterStatus && filterStatus !== 'all') params.append('status', filterStatus);
      if (params.toString()) url += `?${params.toString()}`;

      const response = await fetch(url, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setCodes(data);
      }
    } catch (error) {
      console.error('Error fetching codes:', error);
      toast.error('Failed to load validation codes');
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await fetch(`${API}/validation-codes/stats`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setStats(data);
      }
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      const response = await fetch(`${API}/validation-codes`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        const data = await response.json();
        toast.success(`Generated ${data.count} validation code(s)`);
        setDialogOpen(false);
        setFormData({
          branch_id: '',
          code_type: 'alphanumeric',
          quantity: 1
        });
        fetchCodes();
        fetchStats();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to generate codes');
      }
    } catch (error) {
      console.error('Error generating codes:', error);
      toast.error('Failed to generate codes');
    }
  };

  const copyCode = (code) => {
    navigator.clipboard.writeText(code);
    toast.success('Code copied to clipboard');
  };

  const viewQRCode = (code) => {
    setSelectedCode(code);
    setQrDialogOpen(true);
  };

  const downloadQRCode = () => {
    if (!selectedCode?.qr_code) return;
    
    const link = document.createElement('a');
    link.href = `data:image/png;base64,${selectedCode.qr_code}`;
    link.download = `qr-${selectedCode.code}.png`;
    link.click();
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'active':
        return <span className="badge badge-active"><CheckCircle className="w-3 h-3 mr-1" /> Active</span>;
      case 'used':
        return <span className="badge badge-used"><XCircle className="w-3 h-3 mr-1" /> Used</span>;
      case 'expired':
        return <span className="badge badge-expired"><Clock className="w-3 h-3 mr-1" /> Expired</span>;
      default:
        return <span className="badge">{status}</span>;
    }
  };

  const formatDate = (dateString) => {
    try {
      return format(new Date(dateString), 'MMM d, yyyy HH:mm');
    } catch {
      return dateString;
    }
  };

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="validation-codes-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-[#0F172A] font-['Montserrat']" data-testid="codes-title">
              Validation Codes
            </h1>
            <p className="text-[#64748B] mt-1">
              Generate and manage POS validation codes for survey access
            </p>
          </div>
          
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button 
                className="bg-[#1A3E6F] hover:bg-[#122c50] text-white font-['Montserrat'] font-semibold"
                data-testid="generate-codes-btn"
              >
                <Plus className="w-4 h-4 mr-2" />
                Generate Codes
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-[400px]">
              <DialogHeader>
                <DialogTitle className="font-['Montserrat'] text-[#0F172A]">
                  Generate Validation Codes
                </DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4 mt-4">
                <div className="space-y-2">
                  <Label htmlFor="branch">Branch *</Label>
                  <Select 
                    value={formData.branch_id} 
                    onValueChange={(value) => setFormData({ ...formData, branch_id: value })}
                    required
                  >
                    <SelectTrigger data-testid="code-branch-select">
                      <SelectValue placeholder="Select a branch" />
                    </SelectTrigger>
                    <SelectContent>
                      {branches.map((branch) => (
                        <SelectItem key={branch.branch_id} value={branch.branch_id}>
                          {branch.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="code_type">Code Type</Label>
                  <Select 
                    value={formData.code_type} 
                    onValueChange={(value) => setFormData({ ...formData, code_type: value })}
                  >
                    <SelectTrigger data-testid="code-type-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="alphanumeric">Alphanumeric (A1B2C3D4)</SelectItem>
                      <SelectItem value="numeric">Numeric (12345678)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="quantity">Quantity (1-100)</Label>
                  <Input
                    id="quantity"
                    type="number"
                    min="1"
                    max="100"
                    value={formData.quantity}
                    onChange={(e) => setFormData({ ...formData, quantity: parseInt(e.target.value) || 1 })}
                    data-testid="code-quantity-input"
                  />
                </div>

                <DialogFooter>
                  <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                    Cancel
                  </Button>
                  <Button 
                    type="submit" 
                    className="bg-[#1A3E6F] hover:bg-[#122c50]"
                    disabled={!formData.branch_id}
                    data-testid="submit-codes-btn"
                  >
                    Generate
                  </Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          <Card className="border-[#E2E8F0] shadow-sm" data-testid="stats-total">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-[#1A3E6F]/10 rounded-lg flex items-center justify-center">
                  <QrCode className="w-5 h-5 text-[#1A3E6F]" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-[#0F172A] font-['Montserrat']">{stats.total}</p>
                  <p className="text-xs text-[#64748B]">Total</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-[#E2E8F0] shadow-sm" data-testid="stats-active">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-[#4EBC87]/15 rounded-lg flex items-center justify-center">
                  <CheckCircle className="w-5 h-5 text-[#4EBC87]" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-[#0F172A] font-['Montserrat']">{stats.active}</p>
                  <p className="text-xs text-[#64748B]">Active</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-[#E2E8F0] shadow-sm" data-testid="stats-used">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-[#5BC4BF]/15 rounded-lg flex items-center justify-center">
                  <XCircle className="w-5 h-5 text-[#5BC4BF]" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-[#0F172A] font-['Montserrat']">{stats.used}</p>
                  <p className="text-xs text-[#64748B]">Used</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-[#E2E8F0] shadow-sm" data-testid="stats-expired">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-[#B4B4B4]/20 rounded-lg flex items-center justify-center">
                  <Clock className="w-5 h-5 text-[#64748B]" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-[#0F172A] font-['Montserrat']">{stats.expired}</p>
                  <p className="text-xs text-[#64748B]">Expired</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Filters */}
        <Card className="border-[#E2E8F0] shadow-sm">
          <CardContent className="p-4">
            <div className="flex flex-col sm:flex-row gap-4">
              <div className="flex items-center gap-2 text-sm text-[#64748B]">
                <Filter className="w-4 h-4" />
                <span>Filter:</span>
              </div>
              <div className="flex flex-wrap gap-3">
                <Select value={filterBranch} onValueChange={setFilterBranch}>
                  <SelectTrigger className="w-[180px]" data-testid="filter-branch">
                    <SelectValue placeholder="All branches" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All branches</SelectItem>
                    {branches.map((branch) => (
                      <SelectItem key={branch.branch_id} value={branch.branch_id}>
                        {branch.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>

                <Select value={filterStatus} onValueChange={setFilterStatus}>
                  <SelectTrigger className="w-[150px]" data-testid="filter-status">
                    <SelectValue placeholder="All statuses" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All statuses</SelectItem>
                    <SelectItem value="active">Active</SelectItem>
                    <SelectItem value="used">Used</SelectItem>
                    <SelectItem value="expired">Expired</SelectItem>
                  </SelectContent>
                </Select>

                {(filterBranch !== 'all' || filterStatus !== 'all') && (
                  <Button 
                    variant="ghost" 
                    size="sm"
                    onClick={() => { setFilterBranch('all'); setFilterStatus('all'); }}
                  >
                    Clear filters
                  </Button>
                )}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Codes Table */}
        <Card className="border-[#E2E8F0] shadow-sm">
          <CardContent className="p-0">
            {loading ? (
              <div className="p-8 text-center">
                <div className="loading-spinner mx-auto mb-4"></div>
                <p className="text-[#64748B]">Loading codes...</p>
              </div>
            ) : codes.length === 0 ? (
              <div className="p-12 text-center">
                <QrCode className="w-12 h-12 text-[#B4B4B4] mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-[#0F172A] font-['Montserrat']">No validation codes</h3>
                <p className="text-[#64748B] mt-1 mb-4">Generate codes for your customers to access surveys</p>
                <Button 
                  onClick={() => setDialogOpen(true)}
                  className="bg-[#1A3E6F] hover:bg-[#122c50]"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Generate Codes
                </Button>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="font-['Montserrat']">Code</TableHead>
                      <TableHead className="font-['Montserrat']">QR Code</TableHead>
                      <TableHead className="font-['Montserrat']">Branch</TableHead>
                      <TableHead className="font-['Montserrat']">Status</TableHead>
                      <TableHead className="font-['Montserrat']">Expires</TableHead>
                      <TableHead className="font-['Montserrat']">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {codes.map((code) => (
                      <TableRow key={code.code_id} data-testid={`code-row-${code.code_id}`}>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <code className="bg-[#F8F9FA] px-3 py-1 rounded font-mono text-sm text-[#0F172A]">
                              {code.code}
                            </code>
                          </div>
                        </TableCell>
                        <TableCell>
                          {code.qr_code ? (
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => viewQRCode(code)}
                              className="border-[#5BC4BF] text-[#5BC4BF] hover:bg-[#5BC4BF]/10"
                              data-testid={`view-qr-${code.code_id}`}
                            >
                              <QrCode className="w-4 h-4 mr-1" />
                              View
                            </Button>
                          ) : (
                            <span className="text-[#B4B4B4] text-sm">—</span>
                          )}
                        </TableCell>
                        <TableCell>
                          {branches.find(b => b.branch_id === code.branch_id)?.name || code.branch_id}
                        </TableCell>
                        <TableCell>{getStatusBadge(code.status)}</TableCell>
                        <TableCell className="text-sm text-[#64748B]">
                          {formatDate(code.expires_at)}
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-1">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => copyCode(code.code)}
                              className="text-[#64748B] hover:text-[#1A3E6F]"
                              data-testid={`copy-code-${code.code_id}`}
                            >
                              <Copy className="w-4 h-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>

        {/* QR Code Dialog */}
        <Dialog open={qrDialogOpen} onOpenChange={setQrDialogOpen}>
          <DialogContent className="sm:max-w-[400px]">
            <DialogHeader>
              <DialogTitle className="font-['Montserrat'] text-[#0F172A]">
                QR Code - {selectedCode?.code}
              </DialogTitle>
            </DialogHeader>
            <div className="flex flex-col items-center py-6">
              {selectedCode?.qr_code ? (
                <>
                  <div className="bg-white p-4 rounded-xl border border-[#E2E8F0] shadow-sm">
                    <img 
                      src={`data:image/png;base64,${selectedCode.qr_code}`}
                      alt={`QR Code for ${selectedCode.code}`}
                      className="w-48 h-48"
                      data-testid="qr-code-image"
                    />
                  </div>
                  <p className="text-sm text-[#64748B] mt-4 text-center">
                    Print this QR code on the receipt. Customer scans → survey starts automatically.
                  </p>
                  <p className="text-xs text-[#B4B4B4] mt-2 break-all text-center max-w-[300px]">
                    {selectedCode.qr_url}
                  </p>
                  <div className="flex gap-3 mt-6">
                    <Button
                      onClick={downloadQRCode}
                      className="bg-[#1A3E6F] hover:bg-[#122c50]"
                      data-testid="download-qr-btn"
                    >
                      <Download className="w-4 h-4 mr-2" />
                      Download
                    </Button>
                  </div>
                </>
              ) : (
                <div className="text-center">
                  <QrCode className="w-12 h-12 text-[#B4B4B4] mx-auto mb-4" />
                  <p className="text-[#64748B]">No QR code available</p>
                </div>
              )}
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
}
